import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { TimeRecord, Project } from '@/types';
import { format } from 'date-fns';

interface TimeRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<TimeRecord>) => Promise<void>;
  record?: TimeRecord;
  projects: Project[];
  isProcessing: boolean;
}

export function TimeRecordModal({
  isOpen,
  onClose,
  onSubmit,
  record,
  projects,
  isProcessing,
}: TimeRecordModalProps) {
  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    endTime: '',
    projectId: '',
    categories: [] as string[],
    memo: '',
  });

  useEffect(() => {
    if (record) {
      // 編集モード: 既存レコードの値を設定
      const clockInDate = record.clockInTime.toDate();
      const clockOutDate = record.clockOutTime?.toDate();

      setFormData({
        date: format(clockInDate, 'yyyy-MM-dd'),
        startTime: format(clockInDate, 'HH:mm'),
        endTime: clockOutDate ? format(clockOutDate, 'HH:mm') : '',
        projectId: record.projectId || '',
        categories: record.categories || [],
        memo: record.memo || '',
      });
    } else {
      // 新規作成モード: デフォルト値
      const now = new Date();
      setFormData({
        date: format(now, 'yyyy-MM-dd'),
        startTime: format(now, 'HH:mm'),
        endTime: '',
        projectId: '',
        categories: [],
        memo: '',
      });
    }
  }, [record]);

  const handleCategoryToggle = (category: string) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter((c) => c !== category)
        : [...prev.categories, category],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // バリデーション
    if (!formData.date || !formData.startTime) {
      alert('日付と開始時刻は必須です');
      return;
    }

    if (formData.endTime && formData.startTime >= formData.endTime) {
      alert('終了時刻は開始時刻より後である必要があります');
      return;
    }

    // Timestampオブジェクトを作成
    const clockInTime = new Date(`${formData.date}T${formData.startTime}`);
    const clockOutTime = formData.endTime
      ? new Date(`${formData.date}T${formData.endTime}`)
      : null;

    // totalMinutesを計算
    const totalMinutes = clockOutTime
      ? Math.floor((clockOutTime.getTime() - clockInTime.getTime()) / (1000 * 60))
      : 0;

    // プロジェクト情報を取得
    const selectedProject = projects.find((p) => p.id === formData.projectId);

    const data: Partial<TimeRecord> = {
      clockInTime: clockInTime as any,
      clockOutTime: clockOutTime as any,
      projectId: formData.projectId || undefined,
      project: selectedProject
        ? {
            id: selectedProject.id,
            name: selectedProject.name,
            code: selectedProject.code,
            client: selectedProject.client,
          }
        : undefined,
      categories: formData.categories,
      memo: formData.memo,
      totalMinutes: clockOutTime ? totalMinutes : undefined,
      isManualEntry: !record, // 新規作成の場合は手動入力フラグをセット
    };

    await onSubmit(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* ヘッダー */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {record ? '稼働記録を編集' : '稼働記録を追加'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isProcessing}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* フォーム */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 日付 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              日付 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
              disabled={isProcessing}
            />
          </div>

          {/* 時刻 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                開始時刻 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
                disabled={isProcessing}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                終了時刻
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={isProcessing}
              />
            </div>
          </div>

          {/* プロジェクト */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              プロジェクト
            </label>
            <select
              value={formData.projectId}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={isProcessing}
            >
              <option value="">未選択</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code ? `${project.code} - ${project.name}` : project.name}
                </option>
              ))}
            </select>
          </div>

          {/* カテゴリ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              カテゴリ
            </label>
            <div className="flex flex-wrap gap-3">
              {['会議', '作業', 'その他'].map((category) => (
                <label
                  key={category}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.categories.includes(category)}
                    onChange={() => handleCategoryToggle(category)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    disabled={isProcessing}
                  />
                  <span className="text-sm text-gray-700">{category}</span>
                </label>
              ))}
            </div>
          </div>

          {/* メモ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              備考
            </label>
            <textarea
              value={formData.memo}
              onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder="メモや備考を入力..."
              disabled={isProcessing}
            />
          </div>

          {/* 編集履歴 */}
          {record?.editHistory && record.editHistory.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">編集履歴</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {record.editHistory.map((history, index) => {
                  // 変更内容を人間が読める形式に変換
                  const formatChanges = (changes: any) => {
                    if (!changes) return '';
                    const parts: string[] = [];
                    if (changes.clockInTime) parts.push('開始時刻');
                    if (changes.clockOutTime) parts.push('終了時刻');
                    if (changes.projectId) parts.push('プロジェクト');
                    if (changes.categories) parts.push('カテゴリ');
                    if (changes.memo !== undefined) parts.push('備考');
                    return parts.length > 0 ? parts.join(', ') : '詳細変更';
                  };

                  return (
                    <div key={index} className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                      <div className="font-medium">
                        {format(history.editedAt.toDate(), 'yyyy/MM/dd HH:mm')}
                      </div>
                      <div>{history.editedBy}</div>
                      <div className="mt-1 text-gray-400">
                        変更: {formatChanges(history.changes)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ボタン */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isProcessing}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isProcessing}
            >
              {isProcessing ? '保存中...' : record ? '更新' : '追加'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
